"use client";

import { useEffect, useRef } from "react";
import type { CSSProperties, ReactNode } from "react";
import Link from "next/link";
import "./upgrades-migrations.css";

/* Images — public/assets/images/ se serve hoti hain (import nahi, seedha URL path) */
const IMG = "/assets/images";
/* file names me space / special characters ho sakte hain, isliye encode karke path banate hain */
const img = (name: string) => encodeURI(`${IMG}/${name}`);
const imgHero = img("upgrades-migrations-hero.jpg");
const imgChoose = img("Real-Business-Understanding.jpg");
const imgCta = img("upgrades-migrations-cta.jpg");

/* All styling lives in upgrades-migrations.css (no styling in this file). */

/* ---------- Scroll reveal (bottom to top) ---------- */

function useScrollReveal() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const root = ref.current;
    if (!root) return;
    const items = root.querySelectorAll<HTMLElement>("[data-reveal]");

    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce || !("IntersectionObserver" in window)) {
      items.forEach((el) => el.classList.add("is-visible"));
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -60px 0px" }
    );

    items.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  return ref;
}

const delay = (i: number, step = 80, max = 400): CSSProperties =>
  ({ "--um-delay": `${Math.min(i * step, max)}ms` } as CSSProperties);

/* ---------- Icons ---------- */

type IconKey =
  | "search"
  | "briefcase"
  | "target"
  | "people"
  | "assessment"
  | "convert"
  | "layers"
  | "database"
  | "link"
  | "testing"
  | "system"
  | "newimpl"
  | "selective"
  | "landscape"
  | "cloud"
  | "shield"
  | "continuity"
  | "simplify"
  | "confidence"
  | "flexibility"
  | "cost"
  | "impact"
  | "endtoend"
  | "delivery"
  | "value"
  | "send";

function Icon({ name }: { name: IconKey }) {
  const paths: Record<IconKey, ReactNode> = {
    search: (
      <>
        <circle cx="11" cy="11" r="6.5" />
        <path d="m20 20-4.2-4.2" />
      </>
    ),
    briefcase: (
      <>
        <rect x="3" y="7" width="18" height="13" rx="2" />
        <path d="M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2M3 13h18" />
      </>
    ),
    target: (
      <>
        <circle cx="12" cy="12" r="8.5" />
        <circle cx="12" cy="12" r="4.5" />
        <circle cx="12" cy="12" r="1" />
      </>
    ),
    people: (
      <>
        <circle cx="9" cy="8" r="3" />
        <path d="M3 20c.4-3.6 2.6-5.4 6-5.4s5.6 1.8 6 5.4M16 5.5a3 3 0 0 1 0 5.8M18 14.8c1.8.6 2.8 2.3 3 5.2" />
      </>
    ),
    assessment: (
      <>
        <path d="M12 3 4.5 6v5.5c0 4.6 3.1 8.1 7.5 9.5 4.4-1.4 7.5-4.9 7.5-9.5V6L12 3Z" />
        <path d="m9 12 2.2 2.2L15.5 10" />
      </>
    ),
    convert: (
      <>
        <path d="M20 11a8 8 0 0 0-14.5-4.2L4 9M4 4v5h5" />
        <path d="M4 13a8 8 0 0 0 14.5 4.2L20 15M20 20v-5h-5" />
      </>
    ),
    layers: (
      <>
        <path d="m12 3 9 5-9 5-9-5 9-5Z" />
        <path d="m3 13 9 5 9-5M3 17.5l9 5 9-5" />
      </>
    ),
    database: (
      <>
        <ellipse cx="12" cy="5.5" rx="7" ry="2.8" />
        <path d="M5 5.5v6c0 1.5 3.1 2.8 7 2.8s7-1.3 7-2.8v-6M5 11.5v6c0 1.5 3.1 2.8 7 2.8s7-1.3 7-2.8v-6" />
      </>
    ),
    link: (
      <>
        <path d="M10 14a4.5 4.5 0 0 0 6.4 0l3-3a4.5 4.5 0 0 0-6.4-6.4l-1 1" />
        <path d="M14 10a4.5 4.5 0 0 0-6.4 0l-3 3a4.5 4.5 0 0 0 6.4 6.4l1-1" />
      </>
    ),
    testing: (
      <>
        <rect x="5" y="4" width="14" height="17" rx="2" />
        <path d="M9 4V3h6v1M9 12l2 2 4-4M9 17h6" />
      </>
    ),
    system: (
      <>
        <rect x="4" y="4" width="16" height="12" rx="2" />
        <path d="M8 20h8M12 16v4M9 10l2 2 4-4" />
      </>
    ),
    newimpl: (
      <>
        <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8l-5-5Z" />
        <path d="M14 3v5h5M12 11v6M9 14h6" />
      </>
    ),
    selective: (
      <>
        <path d="M4 6h16M7 12h10M10 18h4" />
      </>
    ),
    landscape: (
      <>
        <rect x="3" y="3" width="7" height="7" rx="1" />
        <rect x="14" y="3" width="7" height="7" rx="1" />
        <rect x="8.5" y="14" width="7" height="7" rx="1" />
        <path d="M6.5 10v2h11v-2M12 12v2" />
      </>
    ),
    cloud: <path d="M7 18a4.5 4.5 0 0 1-.6-8.96A6 6 0 0 1 18 9.5a4.2 4.2 0 0 1-.5 8.5H7Z" />,
    shield: (
      <>
        <path d="M12 3 4.5 6v5.5c0 4.6 3.1 8.1 7.5 9.5 4.4-1.4 7.5-4.9 7.5-9.5V6L12 3Z" />
        <path d="M12 8v4M12 15.5v.5" />
      </>
    ),
    continuity: (
      <>
        <circle cx="12" cy="12" r="3" />
        <path d="M12 3v3M12 18v3M3 12h3M18 12h3M5.6 5.6l2.1 2.1M16.3 16.3l2.1 2.1M18.4 5.6l-2.1 2.1M7.7 16.3l-2.1 2.1" />
      </>
    ),
    simplify: (
      <>
        <rect x="4" y="4" width="16" height="16" rx="2" />
        <path d="M12 8v8M8 12h8" />
      </>
    ),
    confidence: (
      <>
        <path d="M12 3 4.5 6v5.5c0 4.6 3.1 8.1 7.5 9.5 4.4-1.4 7.5-4.9 7.5-9.5V6L12 3Z" />
        <circle cx="12" cy="11" r="2.2" />
      </>
    ),
    flexibility: (
      <>
        <circle cx="12" cy="12" r="9" />
        <path d="M3 12h18M12 3c2.6 2.6 3.8 5.6 3.8 9S14.6 18.4 12 21c-2.6-2.6-3.8-5.6-3.8-9S9.4 5.6 12 3Z" />
      </>
    ),
    cost: (
      <>
        <path d="M4 20V9l8-5 8 5v11M2 20h20" />
        <path d="M12 11v6M10 13.5c0-1 .9-1.5 2-1.5s2 .5 2 1.5-.9 1.2-2 1.5-2 .5-2 1.5.9 1.5 2 1.5 2-.5 2-1.5" />
      </>
    ),
    impact: (
      <>
        <path d="M3 17l5-5 4 4 8-9" />
        <path d="M15 7h5v5" />
      </>
    ),
    endtoend: (
      <>
        <path d="M12 3 4.5 6v5.5c0 4.6 3.1 8.1 7.5 9.5 4.4-1.4 7.5-4.9 7.5-9.5V6L12 3Z" />
        <path d="M9 12h6M12 9v6" />
      </>
    ),
    delivery: (
      <>
        <rect x="5" y="4" width="14" height="17" rx="2" />
        <path d="M9 9h6M9 13h6M9 17h3" />
      </>
    ),
    value: (
      <>
        <path d="M20 12a8 8 0 1 1-3-6.2" />
        <path d="M20 4v5h-5M9 12l2 2 4-4" />
      </>
    ),
    send: <path d="M5 12h14M13 6l6 6-6 6" />,
  };

  return (
    <svg
      className="um-icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {paths[name]}
    </svg>
  );
}

/* ============================== DATA ============================== */

const capabilities: { icon: IconKey; title: string; text: string; href: string }[] = [
  {
    icon: "assessment",
    title: "Migration Assessment",
    text: "Evaluate your current landscape, technical dependencies, custom code and business readiness.",
    href: "/solutions/migration-assessment",
  },
  {
    icon: "convert",
    title: "ECC to S/4HANA Conversion",
    text: "Plan and execute system conversion with minimal disruption and clear business outcomes.",
    href: "/solutions/ecc-to-s4hana-conversion",
  },
  {
    icon: "layers",
    title: "Greenfield & Brownfield",
    text: "Design the right approach based on your business needs, existing investments and target outcomes.",
    href: "/solutions/greenfield-brownfield",
  },
  {
    icon: "database",
    title: "Data Migration & Reconciliation",
    text: "Prepare, cleanse, map and validate data for accurate and reliable migration.",
    href: "/solutions/data-migration",
  },
  {
    icon: "link",
    title: "Integration & Custom Code",
    text: "Assess and adapt interfaces, enhancements and custom code for the new environment.",
    href: "/solutions/integration-custom-code",
  },
  {
    icon: "testing",
    title: "Testing & Cutover Planning",
    text: "Coordinate testing, rehearsals and go-live activities to ensure business continuity.",
    href: "/solutions/testing-cutover",
  },
];

const options: { icon: IconKey; title: string; text: string }[] = [
  { icon: "system", title: "System Conversion", text: "Transform your existing SAP landscape to S/4HANA with reduced complexity." },
  { icon: "newimpl", title: "New Implementation", text: "Build a new S/4HANA environment aligned with future business needs." },
  { icon: "selective", title: "Selective Data Transition", text: "Migrate relevant data, processes and organisational structures." },
  { icon: "landscape", title: "Landscape Consolidation", text: "Simplify multiple SAP systems into a unified environment." },
  { icon: "cloud", title: "Cloud Migration", text: "Plan your move to cloud with the right deployment model." },
];

const steps = [
  { title: "Discover", text: "Understand your current systems, data, integrations and business requirements." },
  { title: "Evaluate", text: "Compare migration options, assess feasibility and define the roadmap." },
  { title: "Prepare", text: "Plan data, custom code, interfaces, testing and cutover strategy." },
  { title: "Migrate", text: "Execute migration with controlled validation and issue resolution." },
  { title: "Stabilize", text: "Support users, monitor performance and optimize the new environment." },
];

const benefits: { icon: IconKey; text: string }[] = [
  { icon: "shield", text: "Reduced operational risk" },
  { icon: "continuity", text: "Improved business continuity" },
  { icon: "simplify", text: "Simplified landscape" },
  { icon: "confidence", text: "Higher data confidence" },
  { icon: "flexibility", text: "Greater flexibility" },
  { icon: "cost", text: "Lower total cost of ownership" },
];

const scenarios = [
  { title: "ECC Modernization", text: "Existing ERP requires modernization.", image: img("Transformation.jpg"), href: "/use-cases/ecc-modernization" },
  { title: "Multi-System Consolidation", text: "Different business units operate separate systems.", image: img("mlti-system-upgrades-migrations.jpg"), href: "/use-cases/multi-system-consolidation" },
  { title: "Historical Data Migration", text: "Large volumes of inconsistent legacy data.", image: img("data_sap_bdc.jpg"), href: "/use-cases/historical-data-migration" },
  { title: "Custom Code Modernization", text: "Enhancements complicate upgrades.", image: img("Custom-Code-upgrades-migrations.jpg"), href: "/use-cases/custom-code-modernization" },
  { title: "Business-Critical Cutover", text: "Downtime could disrupt operations.", image: img("Business-Critical-upgrades-migrations.jpg"), href: "/use-cases/business-critical-cutover" },
  { title: "Cloud Transition", text: "Move to cloud with the right deployment model.", image: img("Cloud-Transition-upgrades-migrations.jpg"), href: "/use-cases/cloud-transition" },
];

const why: { icon: IconKey; title: string; text: string }[] = [
  { icon: "impact", title: "Business impact first", text: "We assess operational needs, not just technical readiness." },
  { icon: "endtoend", title: "End-to-end perspective", text: "We consider SAP and non-SAP systems, data and integrations." },
  { icon: "database", title: "Data-focused approach", text: "We prioritize data integrity, reconciliation and validation." },
  { icon: "delivery", title: "Practical delivery", text: "We plan for dependencies, adoption and business continuity." },
  { icon: "value", title: "Continuous value", text: "We support stabilization and ongoing optimization after go-live." },
];

/* ============================== PAGE ============================== */

export default function UpgradesMigrationsPage() {
  const ref = useScrollReveal();
  return (
    <div className="um-page" ref={ref}>
      <main>
        {/* ============================= HERO ============================= */}
        <section className="um-hero" data-purpose="hero-section" id="top">
          <div className="um-hero-bg" style={{ backgroundImage: `url("${imgHero}")` }}></div>
          <div className="um-hero-overlay"></div>

          <div className="um-hero-layout">
            <div className="um-hero-copy">
              <h1 className="um-hero-title" data-reveal style={delay(0)}>
                Modernise Without Disruption. <span>Migrate With Confidence.</span>
              </h1>

              <p className="um-hero-text" data-reveal style={delay(1, 100)}>
                Orane helps enterprises plan and execute SAP upgrades and migrations with a clear focus on
                business continuity, data integrity and long-term value — from SAP ECC to S/4HANA and beyond.
              </p>

              <div className="um-actions um-hero-actions" data-reveal style={delay(2, 100)}>
                <Link href="/tellusyourproblem#tell-details" className="fp-buttons fp-btn-primary">
                  Discuss Your Migration Challenge <span>→</span>
                </Link>
                <Link href="#capabilities" className="fp-buttons um-btn-outline">
                  Explore Migration Services
                </Link>
              </div>
            </div>

            {/* <p className="um-hero-tagline" data-reveal style={delay(3, 100)}>
              Lower Risk | Cleaner Data | Higher Value
            </p> */}
          </div>
        </section>

        {/* =========================== CAPABILITIES ========================== */}
        <section className="op-section" data-purpose="capabilities" id="capabilities">
          <div className="op-container">
            <div className="sec-head sec-head--row" data-reveal>
              <div>
                <div className="sec-badge">What We Do</div>
                <h2 className="sec-title">Our upgrade and migration capabilities</h2>
              </div>
              <p className="sec-sub">From assessment to post-migration support, we help you modernise with clarity and control.</p>
            </div>

            <div className="um-cap-grid">
              {capabilities.map((c, i) => (
                <article className="um-cap-card" key={c.title} data-reveal style={delay(i, 80)}>
                  <span className="um-cap-icon"><Icon name={c.icon} /></span>
                  <h3>{c.title}</h3>
                  <p>{c.text}</p>
                  {/* <Link href={c.href} className="um-link">Learn more <span>→</span></Link> */}
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* ========================== CHOOSE THE PATH ======================== */}
        <section className="op-section" data-purpose="migration-options">
          <div className="op-container">
            <div className="um-choose">
              <div className="um-choose-copy" data-reveal>
                <div className="sec-badge">Migration Paths</div>
                <h2 className="sec-title">Choose the right migration path for your enterprise.</h2>
                <p className="sec-sub">
                  Every organisation has a different starting point. We help you evaluate and execute the right
                  migration strategy based on your business goals.
                </p>
                {/* <Link href="#migration-options" className="um-link um-link--lg">
                  Explore migration options <span>→</span>
                </Link> */}
              </div>

              <div className="um-choose-image" data-reveal style={delay(1, 100)}>
                <img src={imgChoose} alt="Orane consultants reviewing a migration plan" loading="lazy" />
              </div>

              <div className="um-options" id="migration-options" data-reveal style={delay(2, 100)}>
                <h3 className="um-options-title">Migration options we support:</h3>
                <ul>
                  {options.map((o) => (
                    <li className="um-option" key={o.title}>
                      <span className="um-option-icon"><Icon name={o.icon} /></span>
                      <span className="um-option-title">{o.title}</span>
                      <span className="um-option-text">{o.text}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* ============================= PROCESS ============================= */}
        <section className="op-section" data-purpose="process">
          <div className="op-container">
            <div className="sec-head sec-head--row" data-reveal>
              <div>
                <div className="sec-badge">Our Approach</div>
                <h2 className="sec-title">A controlled transition from assessment to stability.</h2>
              </div>
              <div className="um-head-side">
                <p className="sec-sub">We follow a structured approach to reduce risk and ensure business continuity.</p>
                {/* <Link href="/how-we-work" className="um-link">See how it works <span>→</span></Link> */}
              </div>
            </div>

            <ol className="um-steps">
              {steps.map((s, i) => (
                <li className="um-step" key={s.title} data-reveal style={delay(i, 90)}>
                  <span className="um-step-dot" aria-hidden="true">{String(i + 1).padStart(2, "0")}</span>
                  <h3>{s.title}</h3>
                  <p>{s.text}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* ============================ BENEFITS ============================= */}
        <section className="op-section" data-purpose="benefits">
          <div className="op-container">
            <div className="sec-head sec-head--row" data-reveal>
              <div>
                <div className="sec-badge">Outcomes</div>
                <h2 className="sec-title">Less legacy complexity. More room to improve.</h2>
              </div>
              <p className="sec-sub">Migration value goes beyond go-live — it creates a stronger, more maintainable foundation.</p>
            </div>

            <div className="um-benefit-grid">
              {benefits.map((b, i) => (
                <div className="um-benefit" key={b.text} data-reveal style={delay(i, 70)}>
                  <span className="um-benefit-icon"><Icon name={b.icon} /></span>
                  <span className="um-benefit-text">{b.text}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ============================ SCENARIOS ============================ */}
        <section className="op-section" data-purpose="scenarios">
          <div className="op-container">
            <div className="sec-head sec-head--row" data-reveal>
              <div>
                <div className="sec-badge">Use Cases</div>
                <h2 className="sec-title">Common enterprise migration scenarios</h2>
              </div>
              {/* <Link href="/use-cases" className="um-link">Explore more use cases <span>→</span></Link> */}
            </div>

            <div className="um-scn-grid">
              {scenarios.map((s, i) => (
                <article className="um-scn-card" key={s.title} data-reveal style={delay(i % 6, 70)}>
                  <div className="um-scn-image">
                    <img src={s.image} alt="" aria-hidden="true" loading="lazy" />
                  </div>
                  <div className="um-scn-body">
                    <h3>{s.title}</h3>
                    <p>{s.text}</p>
                    {/* <Link href={s.href} className="um-link">See how we help <span>→</span></Link> */}
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* ============================== WHY ORANE ========================== */}
        <section className="op-section" data-purpose="why-orane">
          <div className="op-container">
            <div className="sec-head sec-head--row" data-reveal>
              <div>
                <div className="sec-badge">Why Orane</div>
                <h2 className="sec-title">Why Orane for upgrades &amp; migrations?</h2>
              </div>
              <p className="sec-sub">We treat migration as a business transition, not just a technical exercise.</p>
            </div>

            <div className="um-why-grid">
              {why.map((w, i) => (
                <article className="um-why-item" key={w.title} data-reveal style={delay(i, 80)}>
                  <span className="um-why-icon"><Icon name={w.icon} /></span>
                  <div className="um-why-copy">
                    <h3>{w.title}</h3>
                    <p>{w.text}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* ========================= FINAL CTA ================================ */}
        <section className="um-cta" data-purpose="final-call-to-action" id="contact">
          <img className="um-cta-bg" src={imgCta} alt="" aria-hidden="true" />
          <div className="um-cta-copy">
            <div className="um-cta-eyebrow" data-reveal>Ready for a smoother, smarter migration?</div>
            <h2 data-reveal style={delay(1, 120)}>Let&apos;s plan your SAP upgrade or migration</h2>
            <p data-reveal style={delay(2, 120)}>
              Tell us what&apos;s holding you back — whether it&apos;s custom code, data, integrations or downtime
              risk. We&apos;ll help you define the right path forward.
            </p>
            <div className="um-actions" data-reveal style={delay(3, 120)}>
              <Link href="/tellusyourproblem#tell-details" className="fp-buttons fp-btn-primary">
                Discuss Your Migration Challenge <span>→</span>
              </Link>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}